import { useDebouncedValue } from '@mantine/hooks'
import type { Address } from '@medplum/fhirtypes'
import {
  hasValidatableContent,
  withStandardizedAddress,
} from '@seen/core/address/addressValidation'
import { invariant } from '@seen/core/invariant'
import { Button } from '@seen/ui'
import { isEqual, pick } from 'lodash-es'
import { type JSX, useEffect } from 'react'
import { useWatch } from 'react-hook-form'
import {
  type AddressFields,
  addressFieldsSchema,
  addressToFields,
  applyAddressFields,
  withoutRedundantSuggestion,
} from '#src/components/address/addressFields.ts'
import { AddressValidationNotice } from '#src/components/address/AddressValidationNotice.tsx'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { Form } from '#src/components/core/form/Form.tsx'
import { FormField } from '#src/components/core/form/FormField.tsx'
import { FormLabel } from '#src/components/core/form/FormLabel.tsx'
import { SubmitButton } from '#src/components/core/form/SubmitButton.tsx'
import { Input } from '#src/components/core/input/Input.tsx'
import { Spinner } from '#src/components/core/Loader.tsx'
import { useSeenApi } from '#src/contexts/seen-api/useSeenApi.ts'
import { useForm } from '#src/hooks/useForm.ts'
import { useQuery } from '#src/hooks/useQuery.ts'

const VALIDATE_DEBOUNCE_MS = 400

export interface AddressConfirmDialogProps {
  // Address being edited; fields it has that the form doesn't edit (use,
  // type, period) are kept on save.
  address?: Address
  // Google place picked from autocomplete; its address replaces the fields.
  placeId?: string
  sessionToken?: string
  title?: string
  onSave: (address: Address) => void
  onCancel: () => void
}

function TextField({
  form,
  name,
  label,
  placeholder,
  autoFocus,
  className,
}: {
  form: ReturnType<typeof useForm<typeof addressFieldsSchema>>
  name: keyof AddressFields
  label: string
  placeholder?: string
  autoFocus?: boolean
  className?: string
}): JSX.Element {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field: { onChange, ...field } }) => (
        <div className={className}>
          <FormLabel className="mb-1 block">{label}</FormLabel>
          <Input
            {...field}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
          />
        </div>
      )}
    />
  )
}

export function AddressConfirmDialog({
  address,
  placeId,
  sessionToken,
  title = 'Confirm address',
  onSave,
  onCancel,
}: AddressConfirmDialogProps): JSX.Element {
  const { ai } = useSeenApi()
  const form = useForm({
    schema: addressFieldsSchema,
    defaultValues: addressToFields(address),
  })

  const place = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps -- the address API is a stable client
    queryKey: ['address-place', placeId, sessionToken],
    queryFn: ({ signal }) => {
      invariant(ai && placeId, 'enabled gates on the address API and place')
      return ai.addressAutocompletePlace({ placeId, sessionToken }, { signal })
    },
    enabled: !!ai && !!placeId,
    staleTime: Infinity,
  })
  const placeAddress = place.data?.address
  // A picked place replaces the postal fields, country included; only the
  // non-postal metadata (use, type, period) carries over from `address`.
  const base = placeAddress
    ? { ...address, country: placeAddress.country ?? 'US' }
    : address
  const isPlaceLoading = !!ai && !!placeId && place.isPending

  useEffect(() => {
    if (placeAddress) {
      form.reset(addressToFields(placeAddress))
    }
  }, [placeAddress, form])

  const fields = useWatch({ control: form.control }) as AddressFields
  const [debouncedFields] = useDebouncedValue(fields, VALIDATE_DEBOUNCE_MS)
  const candidate = pick(applyAddressFields(base, debouncedFields), [
    'line',
    'city',
    'state',
    'postalCode',
    'country',
  ])
  const canValidate =
    !!ai && !isPlaceLoading && hasValidatableContent(candidate)

  const validation = useQuery({
    // oxlint-disable-next-line @tanstack/query/exhaustive-deps -- the address API is a stable client
    queryKey: ['address-validate', candidate],
    queryFn: ({ signal }) => {
      invariant(ai, 'canValidate gates on the address API')
      return ai.addressValidate({ address: candidate }, { signal })
    },
    enabled: canValidate,
    staleTime: Infinity,
  })
  const isSettling = !isEqual(fields, debouncedFields)
  const isChecking = canValidate && (isSettling || validation.isFetching)
  const result =
    canValidate && validation.data
      ? withoutRedundantSuggestion(validation.data, debouncedFields)
      : undefined
  const isVerified = result?.status === 'valid'

  // A verified address is saved in Google's standardized form, so the same
  // address is always stored the same way.
  const save = form.handleSubmit((values) => {
    const standardized =
      isVerified && !isSettling ? result.standardized : undefined
    onSave(
      standardized
        ? withStandardizedAddress(base ?? {}, standardized)
        : applyAddressFields(base, values)
    )
  })

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogDisplay aria-describedby={undefined}>
        <DialogHeader className="py-4 text-lg">
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <Form onSubmit={save}>
          <DialogContent className="gap-4 pt-0">
            {isPlaceLoading ? (
              <div className="flex h-52 items-center justify-center">
                <Spinner className="size-6" />
              </div>
            ) : (
              <>
                <TextField
                  form={form}
                  name="line1"
                  label="Street address"
                  placeholder="100 Main St"
                  autoFocus
                />
                <TextField
                  form={form}
                  name="line2"
                  label="Apt, suite or unit (optional)"
                  placeholder="Apt 4"
                />
                <div className="grid grid-cols-[2fr_1fr_1fr] gap-3">
                  <TextField form={form} name="city" label="City" />
                  <TextField
                    form={form}
                    name="state"
                    label="State"
                    placeholder="CA"
                  />
                  <TextField
                    form={form}
                    name="postalCode"
                    label="ZIP"
                    placeholder="91105"
                  />
                </div>
                {ai && (
                  <AddressValidationNotice
                    result={result}
                    isChecking={isChecking}
                    isError={canValidate && validation.isError}
                    onUseSuggested={(standardized) =>
                      form.reset(addressToFields(standardized))
                    }
                  />
                )}
              </>
            )}
          </DialogContent>
          <DialogFooter className="gap-2 pt-0">
            <Button variant="outline" onClick={onCancel}>
              Cancel
            </Button>
            <SubmitButton disabled={isPlaceLoading}>
              {!ai || isVerified ? 'Save' : 'Save as entered'}
            </SubmitButton>
          </DialogFooter>
        </Form>
      </DialogDisplay>
    </Dialog>
  )
}
